import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FileReaderEvent, UploadProgress, UploadRes } from '../../services/asset-library.model';
import { AssetsService } from '../../services/assets.service';
import { swalMini } from '../../../../shared/utils/utils';
import filesize from 'filesize/lib/filesize.es6.js';
import CryptoJS from 'crypto-js';

@Component({
  selector: 'upload-asset',
  templateUrl: './upload-asset.component.html',
  styleUrls: ['./upload-asset.component.scss'],
  providers: [AssetsService],
})
export class UploadAssetComponent implements OnInit, DoCheck {
  uploadProgressAction: UploadProgress = {
    status: null,
    progress: 0,
    item: null,
  };
  canceled: Boolean = false;

  @Input() startUpload: String;
  @Output() cancelUploadEvHandler = new EventEmitter();
  @Output() finishUploadEvHandler = new EventEmitter();
  @ViewChild('fileUpload') fileUpload: ElementRef;

  constructor(private assetsService: AssetsService) {}

  ngOnInit() {}

  ngDoCheck() {
    if (this.startUpload && !this.uploadProgressAction.status) {
      this.uploadProgressAction.status = 'start';
    }
  }

  dropFiles(e: any) {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files) {
      const files = e.dataTransfer.files;
      this.uploadInProgress(files);
    }
  }

  dragOverHandler(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  uploadFileHandler() {
    const fileUpload = this.fileUpload.nativeElement;
    fileUpload.onchange = () => {
      this.uploadInProgress(fileUpload.files);
    };
    fileUpload.click();
  }

  uploadInProgress(files) {
    if (files.length !== 1) {
      swalMini({
        type: 'error',
        title: 'Please select 1 file!',
      });
      return;
    }

    const file: File = files[0];

    if (file.size > 104857600) {
      swalMini({
        type: 'error',
        title: 'Max file size 100mb',
      });
      return;
    }

    const item = {
      name: file.name,
      file,
      fileId: `${file.name}-${file.lastModified}`,
      uploadCompleted: false,
    };

    this.uploadProgressAction = {
      status: 'progress',
      progress: 0,
      item,
      size: filesize(file.size),
    };

    const reader: FileReader = new FileReader();
    reader.onloadend = (e) => {
      const data = e.target.result;
      const wordArray = CryptoJS.lib.WordArray.create(data);
      const encrypted = CryptoJS.SHA256(wordArray);

      if (this.checkImage(file.type)) {
        const img: HTMLImageElement = new Image();

        img.onload = () => {
          const resolution = `${img.width}x${img.height}`;
          this.uploadFileAction(file, encrypted, resolution);
        };

        img.src = window.URL.createObjectURL(file);
      } else {
        this.uploadFileAction(file, encrypted);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  checkImage(format) {
    return format.split('/')[0] === 'image';
  }

  uploadFileAction(file, encrypted, resolution?) {
    this.assetsService.setFileData(file, encrypted, resolution).subscribe(
      (res: UploadRes) => {
        if (res && res.customMetadata && res.uploadURL) {
          this.assetsService.uploadFile(file, res).subscribe((event) => {
            // @ts-ignore
            const { loaded, total, type } = event;

            if (type === 1) {
              this.uploadProgressAction.progress = Math.ceil((loaded * 100) / total);
              if (this.canceled === true) {
                this.canceled = false;
                throw new Error();
              }
            }

            if (type === 4) {
              if (this.canceled === true) {
                this.assetsService.deleteAsset(res.reference).subscribe(() => {
                  this.canceled = false;
                });
              } else {
                swalMini({
                  type: 'success',
                  title: 'File successfully uploaded',
                });
                this.finishUploadEvHandler.emit();
              }
              this.clearUploadProgressAction();
            }
          });
        } else {
          swalMini({
            type: 'error',
            title: 'Something went wrong',
          });
          this.clearUploadProgressAction();
        }
      },
      (err) => {
        const title =
          err && err.error && err.error.message
            ? err.error.message.replace('<', '&lt;').replace('>', '&gt;')
            // : 'Given file name is not Valid.It should not contain \\, /, no spaces in the beginning or end.';
            : 'Something went wrong';

        swalMini({
          type: 'error',
          title,
        });
        this.clearUploadProgressAction();
      }
    );
  }

  checkUploadProgressStatus() {
    return this.uploadProgressAction.status;
  }

  cancelUpload() {
    if (this.uploadProgressAction.status === 'progress') {
      this.uploadProgressAction.status = 'cancel';
    } else {
      this.clearUploadProgressAction();
    }
  }

  continueUploadHandler() {
    this.uploadProgressAction.status = 'progress';
  }

  cancelUploadHandler() {
    this.canceled = true;
    this.clearUploadProgressAction();
  }

  clearUploadProgressAction() {
    this.cancelUploadEvHandler.emit();
    this.uploadProgressAction = {
      status: null,
      progress: 0,
      item: null,
    };
  }
}
